
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiTrash2, FiMail, FiPhone, FiMapPin, FiClock, FiCheckCircle, FiXCircle, FiAlertCircle } from 'react-icons/fi';
import AdminNavbar from './AdminNavbar';
import AdminSidebar from './AdminSidebar';
import {
    database,
    ref as dbRef,
    set,
    get,
    remove,
    update
} from '../../firebase';

const AdminEnquiries = () => {
    const navigate = useNavigate();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    useEffect(() => {
        // Check authentication
        const isLoggedIn = localStorage.getItem('isLoggedIn');
        if (!isLoggedIn) {
            navigate('/login');
            return;
        }

        loadEnquiries();
    }, [navigate]);

    const loadEnquiries = async () => {
        setLoading(true);
        let enquiriesList = [];
        let applicationsList = [];

        // 1. Fetch General Enquiries
        try {
            const enquiriesRef = dbRef(database, 'shivpratapmultistate/Admin/Enquiries');
            const enquiriesSnapshot = await get(enquiriesRef);

            if (enquiriesSnapshot.exists()) {
                const data = enquiriesSnapshot.val();
                enquiriesList = Object.keys(data).map(key => ({
                    id: key,
                    dbPath: `shivpratapmultistate/Admin/Enquiries/${key}`,
                    source: 'Website Contact', // Default source for existing data
                    ...data[key]
                }));
            }
        } catch (error) {
            console.error('Error loading enquiries:', error);
        }

        // 2. Fetch Legacy Enquiries (Old Path)
        try {
            const legacyEnquiriesRef = dbRef(database, 'shivpratapmultistate/Enquiries');
            const legacySnapshot = await get(legacyEnquiriesRef);

            if (legacySnapshot.exists()) {
                const data = legacySnapshot.val();
                const legacyList = Object.keys(data).map(key => ({
                    id: key,
                    dbPath: `shivpratapmultistate/Enquiries/${key}`,
                    source: 'Website Contact (Legacy)',
                    ...data[key]
                }));
                enquiriesList = [...enquiriesList, ...legacyList];
            }
        } catch (error) {
            console.error('Error loading legacy enquiries:', error);
        }

        // 3. Fetch Job Applications
        try {
            const applicationsRef = dbRef(database, 'shivpratapmultistate/applications');
            const applicationsSnapshot = await get(applicationsRef);

            if (applicationsSnapshot.exists()) {
                const data = applicationsSnapshot.val();
                applicationsList = Object.keys(data).map(key => ({
                    id: key,
                    dbPath: `shivpratapmultistate/applications/${key}`,
                    // Map application fields to enquiry structure
                    name: data[key].name || 'Unknown Candidate',
                    email: data[key].email || '',
                    mobile: data[key].phone || '',
                    city: data[key].address || data[key].city || '',
                    subject: `Job App: ${data[key].jobPosition || 'General'}`,
                    message: `Position: ${data[key].jobPosition}\nExp: ${data[key].yearOfPassing}\nAddress: ${data[key].address}`,
                    source: 'Career Page',
                    status: data[key].status || 'pending',
                    submittedAt: data[key].submittedAt,
                    timestamp: data[key].submittedAt,
                    ...data[key]
                }));
            }
        } catch (error) {
            console.error('Error loading applications:', error);
        }

        // 3. Merge and Sort
        try {
            const combinedList = [...enquiriesList, ...applicationsList];

            combinedList.sort((a, b) => {
                const dateA = a.timestamp ? new Date(a.timestamp) : new Date(a.submittedAt || 0);
                const dateB = b.timestamp ? new Date(b.timestamp) : new Date(b.submittedAt || 0);
                return dateB - dateA; // Newest first
            });

            setEnquiries(combinedList);
        } catch (error) {
            console.error('Error merging data:', error);
            setEnquiries([]); // Fallback
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id, dbPath) => {
        if (!window.confirm('Are you sure you want to delete this enquiry?')) {
            return;
        }

        try {
            // Use the stored dbPath if available (for merged data), otherwise fallback
            const path = dbPath || `shivpratapmultistate/Admin/Enquiries/${id}`;
            const enquiryRef = dbRef(database, path);
            await remove(enquiryRef);
            setEnquiries(prev => prev.filter(item => item.id !== id));
            alert('Item deleted successfully');
        } catch (error) {
            console.error('Error deleting item:', error);
            alert('Error deleting item');
        }
    };

    const handleStatusUpdate = async (id, newStatus, dbPath) => {
        try {
            // Use the stored dbPath if available
            const path = dbPath || `shivpratapmultistate/Admin/Enquiries/${id}`;
            const enquiryRef = dbRef(database, path);
            await update(enquiryRef, { status: newStatus });

            setEnquiries(prev => prev.map(item =>
                item.id === id ? { ...item, status: newStatus } : item
            ));
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Error updating status');
        }
    };

    const filteredEnquiries = enquiries.filter(enquiry => {
        const matchesSearch = (
            (enquiry.name && enquiry.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (enquiry.email && enquiry.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (enquiry.mobile && enquiry.mobile.includes(searchTerm)) ||
            (enquiry.subject && enquiry.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (enquiry.jobPosition && enquiry.jobPosition.toLowerCase().includes(searchTerm.toLowerCase()))
        );

        const matchesStatus = filterStatus === 'all' || enquiry.status === filterStatus;

        return matchesSearch && matchesStatus;
    });

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'bg-green-100 text-green-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'rejected': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
            <AdminNavbar />
            <AdminSidebar collapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />

            <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'} ml-0 pt-16`}>
                <div className="p-4 sm:p-6 lg:p-8">
                    <div className="mb-8">
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 flex items-center mb-2">
                            <FiMail className="mr-3 text-blue-600" />
                            Enquiries Management
                        </h1>
                        <p className="text-gray-600">View and manage customer enquiries</p>
                    </div>

                    {/* Filters and Search */}
                    <div className="bg-white rounded-xl shadow-sm p-4 mb-6 border border-gray-200 flex flex-col md:flex-row gap-4 items-center justify-between">
                        <div className="relative w-full md:w-96">
                            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name, email, phone..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                            />
                        </div>

                        <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto">
                            {['all', 'pending', 'completed', 'rejected'].map(status => (
                                <button
                                    key={status}
                                    onClick={() => setFilterStatus(status)}
                                    className={`px-4 py-2 rounded-lg capitalize whitespace-nowrap transition-colors ${filterStatus === status
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                                        }`}
                                >
                                    {status}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Enquiries List */}
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="text-gray-600 mt-4">Loading enquiries...</p>
                        </div>
                    ) : filteredEnquiries.length === 0 ? (
                        <div className="text-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm">
                            <FiAlertCircle className="text-gray-300 text-6xl mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-gray-700">No Enquiries Found</h3>
                            <p className="text-gray-500 mt-2">No enquiries match your search or filter.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-6">
                            {filteredEnquiries.map((enquiry) => (
                                <div key={enquiry.id} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow duration-300">
                                    <div className="p-6">
                                        <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-4">
                                            <div>
                                                <div className="flex items-center gap-3 mb-1">
                                                    <h3 className="text-lg sm:text-xl font-bold text-gray-800">{enquiry.name}</h3>
                                                    <span className={`text-xs px-2 py-1 rounded-full uppercase font-bold tracking-wider ${getStatusColor(enquiry.status || 'pending')}`}>
                                                        {enquiry.status || 'pending'}
                                                    </span>
                                                </div>
                                                <div className="text-sm text-gray-500 flex items-center gap-2">
                                                    <FiClock size={14} />
                                                    {enquiry.submittedAt}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <select
                                                    value={enquiry.status || 'pending'}
                                                    onChange={(e) => handleStatusUpdate(enquiry.id, e.target.value, enquiry.dbPath)}
                                                    className="text-sm border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2 bg-gray-50"
                                                >
                                                    <option value="pending">Pending</option>
                                                    <option value="completed">Completed</option>
                                                    <option value="rejected">Rejected</option>
                                                </select>
                                                <button
                                                    onClick={() => handleDelete(enquiry.id, enquiry.dbPath)}
                                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Delete Enquiry"
                                                >
                                                    <FiTrash2 size={18} />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 bg-gray-50 p-4 rounded-lg">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                                    <FiMail size={14} />
                                                </div>
                                                <div className="overflow-hidden">
                                                    <p className="text-xs text-gray-500 uppercase font-semibold">Email</p>
                                                    <a href={`mailto:${enquiry.email}`} className="text-blue-600 hover:underline truncate block" title={enquiry.email}>
                                                        {enquiry.email}
                                                    </a>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                                                    <FiPhone size={14} />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500 uppercase font-semibold">Mobile</p>
                                                    <a href={`tel:${enquiry.mobile}`} className="text-gray-800 hover:text-blue-600">
                                                        {enquiry.mobile}
                                                    </a>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                                                    <FiMapPin size={14} />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500 uppercase font-semibold">City</p>
                                                    <p className="text-gray-800">{enquiry.city}</p>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600">
                                                    <FiAlertCircle size={14} />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500 uppercase font-semibold">Subject</p>
                                                    <p className="text-gray-800 truncate" title={enquiry.subject}>{enquiry.subject}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center text-cyan-600">
                                                    <FiCheckCircle size={14} />
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500 uppercase font-semibold">Source</p>
                                                    <p className="text-gray-800 truncate">{enquiry.source || 'Website Form'}</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100">
                                            <p className="text-xs text-blue-400 uppercase font-bold mb-2">Message</p>
                                            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                                                {enquiry.message}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminEnquiries;
